import { useState } from 'react';
import { CheckCircle2, MailWarning } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';

type SendState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * Account-level notice for an address the server has not verified. The
 * server keeps such accounts out of invite acceptance and automatic Runloop
 * provisioning (see `docs/internal/security/authentication.md`), so the
 * notice says what is blocked and how to clear it. Callers gate on
 * `user.emailVerified === false`.
 *
 * Two ways to clear it, chosen by whether `onSend` is given:
 *
 * - **Password account** (`email` + `onSend`): the server mailed a
 *   single-use link at sign-up; the button asks for a fresh one.
 * - **OAuth account** (no `onSend`): the provider did not confirm the
 *   address, so the fix is to sign in again with one that does.
 */
export function EmailNotVerifiedNotice({
  className,
  email,
  onSend,
}: {
  className?: string;
  /** The unverified address, shown in the password-account copy. */
  email?: string;
  /** Ask the server for a new verification email. Rejects on failure. */
  onSend?: () => Promise<unknown>;
}) {
  const [state, setState] = useState<SendState>('idle');

  const send = async () => {
    if (!onSend) return;
    setState('sending');
    try {
      await onSend();
      setState('sent');
    } catch {
      setState('error');
    }
  };

  return (
    <Alert
      className={cn('border-status-accent-orange/30', className)}
      data-testid="email-not-verified-notice"
    >
      <MailWarning className="text-status-accent-orange" />
      <AlertTitle>Email not verified</AlertTitle>
      <AlertDescription className={onSend ? 'space-y-3' : undefined}>
        {onSend ? (
          <>
            <p>
              {state === 'sent'
                ? `Verification email sent to ${email}. Open the link in it to confirm the address. The link works once and expires in 24 hours.`
                : state === 'error'
                  ? 'The verification email could not be sent. Try again in a moment.'
                  : `Open the link we sent to ${email} to confirm this address, or send a new one. Until then, invites sent to it cannot be accepted and automatic Runloop setup is unavailable.`}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void send()}
              disabled={state === 'sending'}
              data-testid="send-verification-email"
            >
              {state === 'sending'
                ? 'Sending...'
                : state === 'sent'
                  ? 'Send again'
                  : 'Send verification email'}
            </Button>
          </>
        ) : (
          <p>
            Your sign-in provider did not confirm this address, so invites sent to it cannot be
            accepted and automatic Runloop setup is unavailable. Sign in with a provider that
            verifies email, such as GitHub or Google, using the same address.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Landing state after the browser follows a verification link. The server
 * consumes the token and redirects to Settings with the outcome in the query
 * string; the page renders this once and clears the marker.
 */
export function EmailVerificationResultNotice({ outcome }: { outcome: 'verified' | 'failed' }) {
  if (outcome === 'verified') {
    return (
      <Alert data-testid="email-verification-result">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Email verified</AlertTitle>
        <AlertDescription>Your address is confirmed. Nothing else to do.</AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive" data-testid="email-verification-result">
      <MailWarning className="h-4 w-4" />
      <AlertTitle>This verification link is not valid</AlertTitle>
      <AlertDescription>
        It may have expired or already been used. Send a new verification email from this page and
        open the newest link.
      </AlertDescription>
    </Alert>
  );
}
